// Sprint-V25 Issue L (Sa 12.09.2026) — OfferSection · Russell-Brunson-Resolution
// Aufbau (mobile-first):
//   1. Section-Kicker („Und ab da laeuft es anders")
//   2. Grosse Resolution-Headline mit Gold-Underline auf Kern-Wort
//   3. Drei Feature-Cards (5 Minuten · Kein Account · Sofort-Ergebnis)
//   4. Final-CTA + Reassurance
// Away-from-Pain-Aufloesung: „warum es immer wieder so laeuft" → „ab da anders"

import React from "react";
import { Clock, Sparkles, Eye, Compass } from "lucide-react";
import { OFFER_FEATURES, CTA_LABEL } from "@/data/landingHookCopy";

const FEATURE_ICON = { clock: Clock, sparkles: Sparkles, eye: Eye, compass: Compass };

export default function OfferSection({ onCtaClick }) {
  return (
    <section
      data-testid="lp-offer-section"
      className="relative w-full overflow-hidden bg-slate-900 dark:bg-slate-950 py-20 sm:py-32"
    >
      {/* Subtle radial glow — Russell-Brunson-Magic auf Dark */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 30%, rgba(251, 191, 36, 0.10), transparent 70%)",
        }}
      />

      <div className="relative mx-auto w-full max-w-md sm:max-w-5xl px-6 sm:px-10 flex flex-col items-center text-center">
        {/* 1. Kicker */}
        <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-amber-300">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400" aria-hidden="true" />
          Und ab da läuft es anders
        </span>

        {/* 2. Resolution-Headline */}
        <h2
          data-testid="lp-offer-headline"
          className="mt-6 sm:mt-10 font-extrabold tracking-tight text-white text-[30px] leading-[1.15] sm:text-[56px] sm:leading-[1.05] lg:text-[64px] max-w-[20ch] sm:max-w-none"
        >
          Du wirst erkennen,
          <br />
          warum es immer{" "}
          <span className="relative inline-block">
            wieder
            <span
              aria-hidden="true"
              className="absolute left-0 right-0 -bottom-1 h-[4px] rounded-full bg-amber-400"
            />
          </span>
          {" "}so läuft.
        </h2>

        {/* 3. Drei Feature-Cards */}
        <div
          data-testid="lp-offer-features"
          className="mt-14 sm:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4 w-full"
        >
          {OFFER_FEATURES.map((f) => {
            const Icon = FEATURE_ICON[f.icon] || Sparkles;
            return (
              <div
                key={f.icon}
                data-testid={`lp-offer-feature-${f.icon}`}
                className="rounded-2xl bg-white/5 backdrop-blur border border-white/10 p-6 flex flex-col items-center text-center hover:bg-white/10 hover:-translate-y-0.5 transition-all"
              >
                <div className="w-12 h-12 rounded-2xl bg-amber-400/10 border border-amber-400/25 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-amber-300" strokeWidth={1.75} />
                </div>
                <div className="mt-4 text-lg font-extrabold text-white">{f.title}</div>
                <div className="mt-1 text-sm text-slate-300 leading-snug">{f.body}</div>
              </div>
            );
          })}
        </div>

        {/* 4. Final-CTA + Reassurance */}
        <button
          type="button"
          data-testid="lp-offer-cta"
          onClick={() => onCtaClick && onCtaClick("offer")}
          className="mt-14 sm:mt-16 w-full sm:w-auto min-h-[56px] px-10 sm:px-14 rounded-2xl font-bold text-[18px] text-slate-950 bg-amber-400 hover:bg-amber-300 shadow-[0_10px_30px_-8px_rgba(251,191,36,0.5)] hover:shadow-[0_16px_40px_-8px_rgba(251,191,36,0.6)] hover:-translate-y-0.5 transition-all focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-200/50"
        >
          {CTA_LABEL}
        </button>
        <p className="mt-4 text-sm text-slate-400">
          Fünf Minuten — und du siehst dich klarer als je zuvor.
        </p>
      </div>
    </section>
  );
}
